/**
 * useAnalysisRange : plage d'analyse effective de la comparaison de sessions.
 *
 * La sélection (mode d'axe et plage) vient du point d'entrée par `range` et
 * `onRangeChange` (URL des routes, état local d'OwnerAsVetShell). Sans
 * `onRangeChange`, la page garde la sélection dans un état local. Tant que
 * l'utilisateur n'a rien choisi (range null), la valeur par défaut dépend du
 * chevauchement des sessions (defaultSelection).
 *
 * Expose la plage résolue, les sessions limitées à leur fenêtre (tableau,
 * coches, PDF), la qualité de chaque session dans la plage et les actions.
 */
import { useMemo, useState } from 'react';
import { ALIGN_MODES } from '../charts/compareAxis';
import { COMPARE_METRICS } from './compareMetrics';
import {
  RANGES, defaultSelection, resolveRange, filterLoaded, coverageRatio,
  retainedDuration, fieldsBelowMinimum, clockCommonRange,
} from './analysisRange';

// Mesures à coche « meilleure valeur » (PM) : seules concernées par le minimum de mesures.
const BEST_FIELDS = COMPARE_METRICS.filter(m => m.lowerIsBetter).map(m => m.field);

/**
 * Analyse d'une sélection sur les sessions chargées (pur, partagé avec le PDF).
 * @param {Array<{session, readings, pagination?}>} loaded
 * @param {Object|null} chosen : sélection explicite, sinon valeur par défaut
 */
export function buildAnalysis(loaded, chosen) {
  const selection = chosen || defaultSelection(loaded);
  const resolved = resolveRange(loaded, selection);
  const filtered = filterLoaded(loaded, resolved);
  const sessions = loaded.map((s, i) => {
    const window = resolved.windows[i];
    return {
      count: filtered[i].readings.length,
      duration: retainedDuration(s, window),
      spanDuration: retainedDuration(s, null),
      coverage: coverageRatio(s.readings, window),
      truncated: Boolean(s.pagination?.paginated),
      total: s.pagination?.total ?? null,
      limit: s.pagination?.limit ?? null,
    };
  });
  return {
    selection,
    resolved,
    filtered,
    sessions,
    overlap: Boolean(clockCommonRange(loaded)),
    hideBestFields: fieldsBelowMinimum(filtered, BEST_FIELDS),
  };
}

export default function useAnalysisRange(loaded, range, onRangeChange) {
  const [localRange, setLocalRange] = useState(null);
  const controlled = typeof onRangeChange === 'function';
  const chosen = controlled ? range : localRange;
  const select = controlled ? onRangeChange : setLocalRange;

  const analysis = useMemo(() => buildAnalysis(loaded, chosen), [loaded, chosen]);

  const { resolved, selection } = analysis;
  // Changement de mode : le raccourci choisi est gardé ; une plage libre
  // (heure réelle seulement) devient la plage commune.
  const setAlign = (align) => select({ align, range: selection.range === RANGES.CUSTOM ? RANGES.COMMON : selection.range });
  const setShortcut = (shortcut) => select({ align: resolved.align, range: shortcut });
  const setCustom = (from, to) => select({ align: ALIGN_MODES.CLOCK, range: RANGES.CUSTOM, from, to });

  return { ...analysis, setAlign, setShortcut, setCustom };
}
